// One reader, for every read.
//
// WHAT IT REPLACES. Five hooks had each written their own version of the same
// four rules, and each had got a different subset of them right:
//
//   `loading` STARTS TRUE. The effect runs after the first render, so a hook
//   that starts at `loading: false` spends one render saying "asked, and there
//   is nothing here" — which a route guard reads and acts on. That render has
//   cost this codebase three bugs: a cold load straight to `/l/<id>` with a
//   valid cookie bounced to the hub twice for exactly this reason.
//
//   AN ANSWER FOR SOMEBODY ELSE IS NOT AN ANSWER. Between the id changing and
//   the effect running for it there is a render where the state still holds
//   the previous subject's value. Comparing identity IN RENDER is the only fix
//   that closes it, because the gap is a render, not a moment — a flag set in
//   the effect has the same hole. `LeaguesProvider.forUserId` was this rule
//   discovered the hard way; here it applies to every reader.
//
//   A REPLACED EFFECT IS NOT A FAILURE. Reporting it flashes an error every
//   time somebody switches league or player. Since 2026-10-07 the request is
//   not aborted at all, only no longer listened to: it is shared with every
//   other reader of the same path (`readStore.ts`), and its answer is kept.
//
//   A FAILED REFRESH KEEPS ITS VALUE (ICD-U01 §4). `loading` is emitted only
//   when there is nothing to show; a reload of data already held stays
//   `ready` and is marked stale, and a reload that fails carries the old value
//   in `lastValue` so a screen can decide between showing it with a warning
//   and showing nothing. None of the five did this: every one of them blanked
//   the screen to refetch it.
//
// The schema is deliberately NOT a dependency of the effect. A caller that
// builds one inline hands over a new object each render, and the effect would
// re-run forever: effect → setState → render → new schema → effect. The first
// headless walk of the player page made 8,720 aborted requests in thirty
// seconds before timing out, for the same reason with a different object.
//
// THE RULES THEMSELVES ARE IN `reader.ts`, and this file is the adapter: it
// turns an effect into events and a render into a call to `shown`. They were
// inside the `useEffect` until 2026-09-20 and therefore untestable — there is
// no DOM here to render a hook into, and a failing refresh over data already
// held is not a state a walk can ask a server for (AUDIT-2026-09-19 F23).
// What is left in this file is the part that genuinely needs React: when to
// ask, when to stop listening, and where the clock comes from. What is shared
// between readers — one request per path, the held answer, how long it is
// fresh — is `readStore.ts`, tested the same way.

import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import type { Snapshot } from '@fsp/domain';
import { failureFrom, get, noConnection, reparse, whenWritten, type Reply } from './api';
import { connection, onReconnect } from './connection';
import { GONE, NOT_NOW } from './kept';
import { onDevice } from './keptIdb';
import { EMPTY, identityOf, reduce, shown, type ReaderEvent, type ReaderState } from './reader';
import { createReadStore } from './readStore';

// ONE STORE FOR THE TAB (`readStore.ts`): every reader of a path shares its
// request and its answer. The request is made as `z.unknown()` and each reader
// checks the body against its own schema (`reparse`), so two readers of one
// path with different schemas each get the outcome they would have alone.
//
// AND THE DEVICE KEEPS EVERY ANSWER (B-557, `kept.ts`): an answer is kept for
// the account that asked, a refusal that says the thing is gone deletes the
// copy, and a request that got no answer, or only "not now", comes back
// carrying the copy, which a reader draws as its failure's `lastValue`, said
// to be from then. The account is taken as the request
// leaves, so an answer that lands after a sign-out is not kept for whoever is
// next.
const store = createReadStore({
  fetch: (path) => {
    const account = onDevice.account();
    return get(path, z.unknown()).then(async (reply): Promise<Reply<unknown>> => {
      if (reply.status === 'ok') {
        onDevice.keep(account, path, { data: reply.data, asOf: reply.asOf });
        return reply;
      }
      if (reply.status === 'refused' && GONE.has(reply.code)) onDevice.drop(account, path);
      if (reply.status === 'refused' && !NOT_NOW.has(reply.code)) return reply;
      const copy = await onDevice.find(account, path);
      return copy === null ? reply : { ...reply, kept: copy };
    });
  },
  after: (ms, fn) => {
    const timer = setTimeout(fn, ms);
    return () => clearTimeout(timer);
  },
});
whenWritten(() => store.wrote());
// WHEN THE CONNECTION COMES BACK, every read on screen asks again, and while
// the site cannot be reached they are asked again on its interval
// (`connection.ts`, B-558): a screen catches up without a reload.
onReconnect(() => store.refreshWatched());
// A HIDDEN PAGE ENDS EVERY WINDOW. The window is a timer, and timers may not
// count a device asleep: a lid closed for three hours would otherwise reopen
// on answers that still call themselves fresh (the review of 2026-10-07).
// Hiding comes before sleep, and before a phone puts the app away.
if (typeof document !== 'undefined')
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') store.expire();
  });

/**
 * Forget every shared answer, and every one this device kept. The session
 * calls it when the person signed in changes, so nothing read as one account
 * is drawn as another, and at a sign-out, so nothing of theirs stays on the
 * device (B-557).
 */
export function forgetReads(): void {
  store.clear();
  void onDevice.forget();
}

/**
 * Ask again every read on screen: a phone's pull to refresh (B-312). What is
 * drawn stays until each answer lands; settles when all have.
 */
export function refreshReads(): Promise<void> {
  return store.refreshWatched();
}

// Six readers of one answer read it once per schema, not six times.
const parsedOf = new WeakMap<object, WeakMap<z.ZodTypeAny, Reply<unknown>>>();
function parsed(reply: Reply<unknown>, schema: z.ZodTypeAny, path: string): Reply<unknown> {
  const asked = `GET /api${path.split('?')[0] ?? path}`;
  if (reply.status !== 'ok' || typeof reply.data !== 'object' || reply.data === null)
    return reparse(reply, schema, asked);
  let bySchema = parsedOf.get(reply.data);
  if (bySchema === undefined) {
    bySchema = new WeakMap();
    parsedOf.set(reply.data, bySchema);
  }
  let out = bySchema.get(schema);
  if (out === undefined) {
    out = reparse(reply, schema, asked);
    bySchema.set(schema, out);
  }
  return out;
}

export interface ReadOptions {
  /**
   * Extra identity, folded in beside the path.
   *
   * For a read whose SUBJECT is not in its URL. `/api/hub` is the same path
   * for every account, so without this, signing into a second account in the
   * same tab would show the first one's leagues until the answer landed.
   */
  key?: string;
  /**
   * A 404 answers `ready(null)` instead of `failed`.
   *
   * For the reads where "no such thing" is an ordinary answer rather than a
   * problem: an invite code that names no league is something the person can
   * fix by retyping it, and calling it a failure sends them back to whoever
   * sent the link.
   */
  emptyOn404?: boolean;
  /**
   * Hold at `loading` and ask nothing yet.
   *
   * For a caller that knows it does not yet know whether there is anything to
   * ask for — the session before `GET /api/me` has answered. Without it, a
   * cold load with a valid cookie and no cached identity reports "you are in
   * no leagues" for one render, which is the guard bug above wearing a hat.
   */
  pending?: boolean;
  /**
   * Ask again whenever this changes, keeping what is held on screen.
   *
   * For a read something ELSE on the page can make stale: a roster after an
   * add, the claims after one is withdrawn (`useMovesVersion` in `moves.ts`).
   * It is a reload, not a new identity — a new identity would blank the
   * screen for a read of the same thing.
   */
  refresh?: number;
}

export interface Read<T> {
  snapshot: Snapshot<T>;
  /** Ask again, keeping what we hold on screen while it runs. */
  reload: () => void;
}

export function useRead<S extends z.ZodTypeAny>(
  path: string | null,
  schema: S,
  options: ReadOptions = {},
): Read<z.output<S> | null> {
  type Value = z.output<S> | null;
  const { key = '', emptyOn404 = false, pending = false, refresh = 0 } = options;
  const identity = identityOf(path, key);

  // ONE PIECE OF STATE, holding both the last answer and the newest good
  // value. `held` was a ref until 2026-09-20 — which changed no render,
  // because every write to it was already paired with a `setAnswer` — and
  // splitting the two put half the state machine somewhere a reducer could
  // not see it.
  const [state, setState] = useState<ReaderState<Value>>(EMPTY);
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const parser = useRef(schema);
  parser.current = schema;

  // What this reader last asked, so a run of the effect can tell a reload
  // (same question, new `nonce` or `refresh`) from a first ask or a re-mount.
  const asked = useRef<{ identity: string; nonce: number; refresh: number } | null>(null);

  useEffect(() => {
    if (path === null || identity === null || pending) return;
    // The effect has been replaced once this is false. Whatever arrives after
    // is answering a question nobody here is asking any more. The request
    // itself is not aborted: another reader may be waiting on it, and its
    // answer is kept for the next one (`readStore.ts`).
    let live = true;
    const eventOf = (reply: Reply<unknown>): ReaderEvent<Value> => {
      const mine = parsed(reply, parser.current, path) as Reply<Value>;
      if (mine.status === 'ok') return { kind: 'ok', identity, value: mine.data, asOf: mine.asOf };
      if (mine.kept !== undefined) {
        // Read against this reader's schema like any answer, and quietly: a
        // copy an older build kept that this one cannot read is no copy, not
        // drift worth a report.
        const copy = reparse(
          { status: 'ok', data: mine.kept.data, asOf: mine.kept.asOf },
          parser.current,
        ) as Reply<Value>;
        const failure = failureFrom(mine);
        return copy.status === 'ok'
          ? { kind: 'fail', identity, failure, kept: { value: copy.data, asOf: copy.asOf } }
          : { kind: 'fail', identity, failure };
      }
      if (emptyOn404 && mine.status === 'refused' && mine.code === 'not_found') {
        // "No such thing" is an ordinary answer for these reads, and it is
        // `ready(null)`. The instant is the one it arrived at, because the
        // payload that would have carried a better one does not exist.
        // The arrival instant, for the reason api.ts gives at its own read:
        // there is no clock port on this side of the wire.
        // eslint-disable-next-line no-restricted-syntax
        const asOf = new Date().toISOString();
        return { kind: 'ok', identity, value: null, asOf };
      }
      return { kind: 'fail', identity, failure: failureFrom(mine) };
    };
    const hear = (reply: Reply<unknown>) => {
      if (!live) return;
      const event = eventOf(reply);
      setState((previous) => reduce(previous, event));
    };
    const unsubscribe = store.subscribe(identity, hear);

    const last = asked.current;
    const force =
      last !== null &&
      last.identity === identity &&
      (last.nonce !== nonce || last.refresh !== refresh);
    asked.current = { identity, nonce, refresh };

    // WHAT IS HELD IS DRAWN AT ONCE (B-263): a screen opened again shows its
    // last answer while the new one comes, not a spinner. A fresh one is the
    // answer, and nothing is asked.
    const held = store.peek(identity);
    if (held !== null) hear(held.reply);
    if (force || held === null || !held.fresh) {
      setState((previous) => reduce(previous, { kind: 'ask', identity }));
      store.ask(identity, path, { force });
      // THE CONNECTION IS KNOWN TO BE DOWN (B-558): what the device kept is
      // drawn now, said to be old, rather than once the request has failed,
      // which on a connection that answers nothing is fifteen seconds away.
      // Only while nothing has answered: the request is still asked, and its
      // answer replaces this.
      if (held === null && connection.isDown()) {
        void onDevice.find(onDevice.account(), path).then((copy) => {
          if (!live || copy === null) return;
          const event = eventOf({ ...noConnection(), kept: copy });
          setState((previous) =>
            previous.answer?.identity === identity ? previous : reduce(previous, event),
          );
        });
      }
    }
    return () => {
      live = false;
      unsubscribe();
    };
    // `identity` is `key` and `path` together, so listing `path` beside it is
    // belt and braces rather than a second trigger. `schema` is absent on
    // purpose and is read through the ref above — see the header. `refresh`
    // is a reload by another name, like `nonce`.
  }, [identity, path, nonce, refresh, pending, emptyOn404]);

  // Nothing to ask for is an ANSWER, and it is the empty one (ICD-U01 §9).
  // Held in a ref so it is the same object on every render: a caller putting
  // the snapshot in a dependency array must not be woken by it.
  const idle = useRef<Snapshot<Value> | null>(null);
  if (idle.current === null) {
    // eslint-disable-next-line no-restricted-syntax
    idle.current = { state: 'ready', value: null, asOf: new Date().toISOString(), stale: false };
  }

  // IN RENDER, not in the effect. An answer that belongs to a different
  // identity is not an answer yet, however recently it arrived.
  const snapshot = shown(state, { identity, pending, idle: idle.current });
  // A HELD ANSWER IS THE FIRST EMISSION, in this render: "synchronously if
  // cached" (ICD-U01 §8). The effect below hands it over too, but only after
  // a frame has been painted, and that frame would say "Loading…" over a
  // screen whose answer is already here.
  if (snapshot.state === 'loading' && path !== null && identity !== null && !pending) {
    const held = store.peek(identity);
    if (held !== null) {
      const mine = heldSnapshot(parsed(held.reply, schema, path), !held.fresh);
      if (mine !== null) return { snapshot: mine as Snapshot<Value>, reload };
    }
  }
  return { snapshot, reload };
}

// One snapshot object per held answer and staleness, so a caller that puts
// the snapshot in a dependency array is not woken by every render of the gap.
const heldSnapshots = new WeakMap<
  object,
  { fresh?: Snapshot<unknown>; stale?: Snapshot<unknown> }
>();
function heldSnapshot(reply: Reply<unknown>, stale: boolean): Snapshot<unknown> | null {
  if (reply.status !== 'ok') return null;
  let pair = heldSnapshots.get(reply);
  if (pair === undefined) {
    pair = {};
    heldSnapshots.set(reply, pair);
  }
  const which = stale ? 'stale' : 'fresh';
  pair[which] ??= { state: 'ready', value: reply.data, asOf: reply.asOf, stale };
  return pair[which];
}
